import React, { useCallback, useState, useEffect, useMemo } from 'react';
import {SearchIcon} from '@contentful/f36-icons';
import {
  FormControl,
  Textarea,
  Checkbox,
  Flex,
  Table,
  TextInput,
  Stack,
  Card,
} from '@contentful/f36-components';
import { css } from 'emotion';
import { useSDK } from '@contentful/react-apps-toolkit';
import { createClient } from 'contentful-management';
import { verify } from '../services/translated';

const ConfigScreen = () => {
  const [parameters, setParameters] = useState({ apiKey: '', askConfirmation: true });
  const [contentTypes, setContentTypes] = useState([]);
  const [editorInterface, setEditorInterface] = useState({});
  const [askConfirmation, setAskConfirmation] = useState(true);
  const [allCheckboxState, setAllCheckboxState] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  const sdk = useSDK();
  // Memoized: createClient() must not run on every render, or the useEffects below
  // (which depend on cma) re-fire every time their own setState triggers a re-render,
  // looping forever.
  const cma = useMemo(
    () => createClient({ apiAdapter: sdk.cmaAdapter }),
    [sdk.cmaAdapter],
  );

  // Load data on mount
  useEffect(() => {
    const loadInitialData = async () => {
      const space = await cma.getSpace(sdk.ids.space);
      const environment = await space.getEnvironment(sdk.ids.environment);
      const { items: contentTypes } = await environment.getContentTypes();
      setFilteredData(contentTypes);
    };
    loadInitialData();
  }, [cma, sdk.ids.environment, sdk.ids.space]);


  // Handle the search functionality
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    // if search term is empty, set filtered data to original data
    if (value === '') {
      setFilteredData(contentTypes);
    } else {
    // if search term is not empty, filter the content types based on the search term
      const filtered = contentTypes.filter((item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    // finally set filtered data to the filtered content types
      setFilteredData(filtered);
    }
  };

  // Sort the content types based on the last updated date
  const handleSort = () => {
    const sortedData = [...filteredData].sort((a, b) => {
      if (sortOrder === 'asc') {
        return new Date(a.sys.updatedAt) - new Date(b.sys.updatedAt);
      } else {
        return new Date(b.sys.updatedAt) - new Date(a.sys.updatedAt);
      }
    });
    setFilteredData(sortedData);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  // Handle the change of the checkbox for each content type
  const onContentTypeChange = (id) => (e) => {
    let newEditorInterface
    // if the checkbox is checked, add the content type to the editor interface
    if(e.target.checked) {
      newEditorInterface = Object.assign({[id]: { sidebar: { position: 1}}}, editorInterface)
    } else {
    // if the checkbox is unchecked, remove the content type from the editor interface
      newEditorInterface = Object.assign({}, editorInterface)
      delete newEditorInterface[id]
    }
    setEditorInterface(newEditorInterface)
  };

  // Handle the select all checkbox
  const handleSelectAll = (e) => {
    const newState = e.target.checked;
    const newEditorInterface = { ...editorInterface };

    filteredData.forEach((item) => {
      if (newState) {
        newEditorInterface[item.sys.id] = { sidebar: { position: 1 } };
      } else {
        delete newEditorInterface[item.sys.id];
      }
    });

    setAllCheckboxState(newState);
    setEditorInterface(newEditorInterface);
  };

  // Send the configuration to the app
  const onConfigure = useCallback(async () => {
    // Verify the API key
    const isApiKeyValid = await verify(parameters.apiKey);
    if (!isApiKeyValid) {
      sdk.notifier.error('Invalid input - api key not valid.');
      return false;
    }

    return {
      // Parameters to be persisted as the app configuration.
      parameters,
      // In case you don't want to submit any update to app
      // locations, you can just pass the currentState as is
      targetState: {
        EditorInterface: {
          ...editorInterface
        }
      },
    };
  }, [parameters, sdk, editorInterface]);

  useEffect(() => {
    // `onConfigure` allows to configure a callback to be
    // invoked when a user attempts to install the app or update
    // its configuration.
    sdk.app.onConfigure(() => onConfigure());
  }, [sdk, onConfigure]);

  useEffect(() => {
    // Functional update form so this effect doesn't need `parameters` in its deps —
    // depending on state it itself sets (via a freshly-allocated object every time)
    // caused an infinite render loop.
    setParameters((prev) => ({ ...prev, askConfirmation }));
  }, [askConfirmation]);

  useEffect(() => {
    (async () => {
      // Get current parameters of the app.
      // If the app is not installed yet, `parameters` will be `null`.
      const currentParameters = await sdk.app.getParameters();
      if (currentParameters) {
        setParameters(currentParameters);
        setAskConfirmation(currentParameters.askConfirmation);
      }

      const currentState = await sdk.app.getCurrentState();

      setEditorInterface(currentState?.EditorInterface || {});

      const space = await cma.getSpace(sdk.ids.space);
      const environment = await space.getEnvironment(sdk.ids.environment);
      const contentTypes = [];
      let lastElement = 0;
      let totals;
      const ELEMENTS_PER_PAGE = 100;
      do {
        const { items, total } = await environment.getContentTypes({
          limit: ELEMENTS_PER_PAGE,
          skip: lastElement,
          order: 'name',
        });
        totals = total;
        lastElement = lastElement + ELEMENTS_PER_PAGE;
        contentTypes.push(...items);
      } while (lastElement < totals);
      setContentTypes(contentTypes);

      // Once preparation has finished, call `setReady` to hide
      // the loading screen and present the app to a user.
      await sdk.app.setReady();
    })();
  }, [cma, sdk]);

  return (
      <Card>
        <Flex flexDirection="column" className={css({marginTop:'5%', marginLeft: '20%', marginRight:'20%'})}>
          <FormControl isRequired>
            <FormControl.Label>TranslationOS API key:</FormControl.Label>
            <Textarea
                value={parameters.apiKey}
                type="text"
                name="apiKey"
                placeholder="API key"
                rows="3"
                style={{ fontFamily: 'monospace' }}
                onChange={(e) => setParameters({ apiKey: e.target.value, askConfirmation: parameters.askConfirmation })}
            />
            <FormControl.HelpText>Please insert API key provided by Translated</FormControl.HelpText>
          </FormControl>
          <FormControl.Label>App will be enabled on the following content models:</FormControl.Label>
          <Stack flexDirection="column">
            <TextInput.Group>
              <TextInput
                  aria-label="search-input"
                  id="search input"
                  value={searchTerm}
                  onChange={handleSearch}
                  placeholder={'Search'}
                  size={'small'}
                  icon={<SearchIcon/>}
              />
            </TextInput.Group>
          </Stack>
          <div
              style={{
                height: '600px',
                overflowY: 'scroll',
                overflowX: 'hidden',
              }}
          >
            <Table.Head isSticky={true}>
              <Table.Row >
                <Table.Cell >
                  <Checkbox
                      name="select-all"
                      isChecked={allCheckboxState}
                      onChange={handleSelectAll}
                  ></Checkbox>
                </Table.Cell>
                <Table.Cell style={{ width: '50%' }}>Name</Table.Cell>
                <Table.Cell style={{ width: '50%', cursor: 'pointer' }} onClick={handleSort}>
                  Last updated {sortOrder === 'asc' ? '↑' : '↓'}
                </Table.Cell>
                <Table.Cell style={{ width: 'auto' }}>Fields</Table.Cell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {filteredData.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan="4" style={{ textAlign: 'center' }}>
                      No results to show
                    </Table.Cell>
                  </Table.Row>
              ) : (
                  filteredData.map((contentType) => (
                      <Table.Row key={contentType.sys.id}>
                        <Table.Cell>
                          <Checkbox
                              name="select-one"
                              key={contentType.sys.id}
                              isChecked={Object.keys(editorInterface).includes(contentType.sys.id)}
                              onChange={onContentTypeChange(contentType.sys.id)}
                          ></Checkbox>
                        </Table.Cell>
                        <Table.Cell>
                          {contentType.name}
                          <br /> <p style={{ fontSize: '11px' }}>{contentType.description}</p>
                        </Table.Cell>
                        <Table.Cell>{new Date(contentType.sys.updatedAt).toLocaleString()}</Table.Cell>
                        <Table.Cell>{contentType.fields.length}</Table.Cell>
                      </Table.Row>
                  ))
              )}
            </Table.Body>
          </div>
        </Flex>
      </Card>
  );
};

export default ConfigScreen;